import React, { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { IconButton } from '../IconButton/IconButton';
import type { IconButtonVariant, IconButtonSize } from '../IconButton/IconButton';
import type { IconProps } from '../../icons';
import '../DropdownButton/DropdownButton.css';

interface DropdownIconButtonProps {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  disabled?: boolean;
  leadingVisual?: IconProps['name'] | React.ReactNode;
  trailingVisual?: IconProps['name'] | React.ReactNode;
  dropdownContent: React.ReactNode;
  ariaLabel?: string;
  // Portal rendering
  portal?: boolean; // default true
  portalContainer?: HTMLElement | (() => HTMLElement | null);
  portalZIndex?: React.CSSProperties['zIndex'];
  positionStrategy?: 'fixed' | 'absolute';
}

export const DropdownIconButton = React.memo(function DropdownIconButton({
  variant = 'primary',
  size = 'md',
  disabled = false,
  leadingVisual,
  trailingVisual = 'ChevronDown',
  dropdownContent,
  ariaLabel,
  portal = true,
  portalContainer,
  portalZIndex = 'var(--z-index-overlay, 1000)',
  positionStrategy = 'fixed',
}: DropdownIconButtonProps) {
  const reactId = useId();
  const menuId = `dropdown-icon-menu-${reactId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'left' | 'right'>('left');
  const [verticalPosition, setVerticalPosition] = useState<'top' | 'bottom'>(
    'bottom',
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [portalPos, setPortalPos] = useState<{
    left: number;
    top: number;
    minWidth: number;
  } | null>(null);

  useEffect(() => setMounted(true), []);

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen && portal) {
      const triggerEl = containerRef.current;
      if (triggerEl) {
        const rect = triggerEl.getBoundingClientRect();
        const gap = 8;
        setPortalPos({
          left: Math.round(rect.left),
          top: Math.round(rect.bottom + gap),
          minWidth: Math.round(rect.width),
        });
      }
    }
    setIsOpen((v) => !v);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insidePortal = menuRef.current?.contains(target as Node) ?? false;
      if (!insideTrigger && !insidePortal) setIsOpen(false);
    };

    const updatePosition = () => {
      const triggerEl = containerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spaceOnRight = viewportWidth - rect.right;
      const spaceOnLeft = rect.left;
      const alignLeft = spaceOnRight >= spaceOnLeft;
      setMenuPosition(alignLeft ? 'left' : 'right');
      if (!portal || !isOpen) return;
      const gap = 8;
      const viewportMargin = 8;
      const width = Math.round(rect.width);
      const menuEl = menuRef.current;
      const menuHeight = menuEl ? menuEl.offsetHeight : 0;
      // Pick vertical placement: prefer below; flip up when the menu would
      // overflow the viewport bottom but fits above. When neither side
      // fully fits, pick the side with more room.
      const spaceBelow = viewportHeight - rect.bottom - viewportMargin - gap;
      const spaceAbove = rect.top - viewportMargin - gap;
      let openAbove = false;
      if (menuHeight > 0) {
        if (menuHeight <= spaceBelow) {
          openAbove = false;
        } else if (menuHeight <= spaceAbove) {
          openAbove = true;
        } else {
          openAbove = spaceAbove > spaceBelow;
        }
      }
      setVerticalPosition(openAbove ? 'top' : 'bottom');
      if (positionStrategy === 'fixed') {
        const top = openAbove
          ? Math.round(rect.top - menuHeight - gap)
          : Math.round(rect.bottom + gap);
        if (alignLeft) {
          setPortalPos({
            left: Math.round(rect.left),
            top,
            minWidth: width,
          });
        } else {
          const left = Math.round(rect.right - width);
          setPortalPos({
            left: Math.max(0, left),
            top,
            minWidth: width,
          });
        }
      } else {
        const containerEl: HTMLElement | null =
          typeof portalContainer === 'function'
            ? (portalContainer() as HTMLElement | null)
            : (portalContainer ?? null);
        const containerRect = containerEl?.getBoundingClientRect();
        const containerLeft = containerRect?.left ?? 0;
        const containerTop = containerRect?.top ?? 0;
        const top = openAbove
          ? Math.round(rect.top - containerTop - menuHeight - gap)
          : Math.round(rect.bottom - containerTop + gap);
        if (alignLeft) {
          setPortalPos({
            left: Math.round(rect.left - containerLeft),
            top,
            minWidth: width,
          });
        } else {
          const left = Math.round(rect.right - containerLeft - width);
          setPortalPos({
            left: Math.max(0, left),
            top,
            minWidth: width,
          });
        }
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      requestAnimationFrame(updatePosition);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      document.addEventListener('mousedown', handleClickOutside, { capture: true });
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handleClickOutside, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, portal, positionStrategy, portalContainer]);

  return (
    <div className="dropdown-button" ref={containerRef} aria-label={ariaLabel}>
      <IconButton
        variant={variant || 'secondary'}
        size={size}
        disabled={disabled}
        leadingVisual={leadingVisual}
        trailingVisual={trailingVisual}
        onClick={toggleDropdown}
        aria-label={typeof leadingVisual === 'string' ? leadingVisual : ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
      />
      {(() => {
        const menu = (
          <motion.ul
            ref={menuRef}
            id={menuId}
            role="menu"
            className={`dropdown dropdown-button__menu menu--${menuPosition} menu--v-${verticalPosition}`}
            initial={{
              opacity: 0,
              scale: 0.85,
            }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.2, ease: [0.77, 0, 0.175, 1] }}
            // transformOrigin must live in `style` (re-applied on every
            // render) rather than `initial` (only applied at mount).
            // verticalPosition is always 'bottom' on first render and
            // can flip to 'top' once updatePosition measures the menu;
            // an `initial`-only origin would never update after that
            // flip, and the menu would still scale in from the top
            // edge instead of the bottom when opening upward.
            style={
              portal && portalPos
                ? ({
                    position: positionStrategy,
                    top: portalPos.top,
                    left: portalPos.left,
                    minWidth: portalPos.minWidth,
                    zIndex: portalZIndex,
                    transformOrigin: `${verticalPosition === 'top' ? 'bottom' : 'top'} ${menuPosition}`,
                    willChange: 'transform, opacity',
                  } as React.CSSProperties)
                : ({
                    transformOrigin: `${verticalPosition === 'top' ? 'bottom' : 'top'} ${menuPosition}`,
                    willChange: 'transform, opacity',
                  } as React.CSSProperties)
            }
            onClick={(event) => {
              const target = event.target as HTMLElement | null;
              if (!target) return;
              if (target.closest('li.dropdown-item')) {
                setIsOpen(false);
                // restore focus to trigger
                const btn = containerRef.current?.querySelector(
                  'button',
                ) as HTMLButtonElement | null;
                btn?.focus();
              }
            }}
          >
            <div className="dropdown-button__content">{dropdownContent}</div>
          </motion.ul>
        );

        if (portal && mounted) {
          const container = (
            typeof portalContainer === 'function'
              ? (portalContainer() ?? document.body)
              : (portalContainer ?? document.body)
          ) as HTMLElement;
          return createPortal(<AnimatePresence>{isOpen && menu}</AnimatePresence>, container);
        }
        return <AnimatePresence>{isOpen && menu}</AnimatePresence>;
      })()}
    </div>
  );
});
